import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { useRuntimeChannel } from '../../contexts/ConsoleContext';
import { downloadChannelLogsCsv, getChannelLogs } from '../../services/auditService';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { ConsolePageShell } from '../console/ConsolePageShell';
import { formatAuditDetails } from '../../utils/auditDetails';

type AuditLogEntry = {
  id: number;
  action: string;
  details: string;
  createdAt: string;
  userId: string | number;
};

const cardSx = {
  borderRadius: 0,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
} as const;

const headerCellSx = {
  fontSize: '0.64rem',
  fontWeight: 900,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
  bgcolor: 'background.default',
};

const mobileLabelSx = {
  fontSize: '0.64rem',
  fontWeight: 900,
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
};

const PAGE_SIZE = 25;

const normaliseAuditPage = (data: any, fallbackPage: number) => {
  if (Array.isArray(data)) {
    return {
      items: data,
      total: data.length,
      page: fallbackPage,
      pageSize: PAGE_SIZE,
      totalPages: Math.max(1, Math.ceil(data.length / PAGE_SIZE)),
    };
  }
  const items = Array.isArray(data?.items) ? data.items : [];
  return {
    items,
    total: Number(data?.total ?? items.length),
    page: Number(data?.page ?? fallbackPage),
    pageSize: Number(data?.pageSize ?? PAGE_SIZE),
    totalPages: Math.max(1, Number(data?.totalPages ?? (Math.ceil(items.length / PAGE_SIZE) || 1))),
  };
};

const safeFilename = (value: string) => value.replace(/[^a-z0-9._-]+/gi, '-').replace(/^-+|-+$/g, '') || 'channel';

const formatAuditDate = (value: string) => formatDateTimeDDMMYY(value);

export const PortalHistory: React.FC = () => {
  const { activeChannel } = useRuntimeChannel();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [activeChannel?.id]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!activeChannel) {
        setLogs([]);
        setTotal(0);
        setTotalPages(1);
        return;
      }
      setLoading(true);
      try {
        const data = await getChannelLogs(activeChannel, { page, limit: PAGE_SIZE });
        if (!cancelled) {
          const parsed = normaliseAuditPage(data, page);
          setLogs(parsed.items);
          setTotal(parsed.total);
          setTotalPages(parsed.totalPages);
        }
      } catch {
        if (!cancelled) {
          setLogs([]);
          setTotal(0);
          setTotalPages(1);
          showSnackbar('Failed to load channel history.', 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [activeChannel, page, showSnackbar]);

  const handleDownloadCsv = async () => {
    if (!activeChannel) return;
    setDownloading(true);
    try {
      const blob = await downloadChannelLogsCsv(activeChannel);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeFilename(activeChannel.name || 'channel')}-audit-history.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSnackbar('Audit CSV downloaded.', 'success');
    } catch {
      showSnackbar('Failed to download audit CSV.', 'error');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <ConsolePageShell
      title="History"
      eyebrow="Channel Audit"
      subtitle={activeChannel ? `Actions, configuration changes, and service events for ${activeChannel.name}.` : 'Select a channel to inspect its audit trail.'}
      actions={activeChannel ? (
        <Tooltip title="Download CSV history">
          <IconButton size="small" onClick={handleDownloadCsv} disabled={downloading} sx={{ borderRadius: 0, border: '1px solid', borderColor: 'divider' }}>
            {downloading ? <CircularProgress size={16} /> : <FileDownloadIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      ) : undefined}
      stats={[
        { label: 'Entries', value: total, helper: 'audit records' },
        { label: 'Channel', value: activeChannel?.name || 'None', helper: 'current selection', tone: activeChannel ? 'success' : 'default' },
      ]}
    >
      {!activeChannel ? (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          Select a channel to view its history.
        </Alert>
      ) : loading ? (
        <Box textAlign="center" py={8}>
          <CircularProgress />
        </Box>
      ) : logs.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 0 }}>
          No audit history recorded for this channel yet.
        </Alert>
      ) : (
        <>
          <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden', display: { xs: 'none', md: 'block' } }}>
            <TableContainer sx={{ maxHeight: '70vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>When</TableCell>
                    <TableCell sx={headerCellSx}>Action</TableCell>
                    <TableCell sx={headerCellSx}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} hover>
                      <TableCell sx={{ fontFamily: "'IBM Plex Mono', 'DM Mono', monospace", fontSize: '0.72rem', color: 'secondary.main', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                        {formatAuditDate(log.createdAt)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: '0.75rem', verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                        {log.action}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.78rem', color: 'text.primary', verticalAlign: 'top' }}>
                        {formatAuditDetails(log.details)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Stack spacing={1} sx={{ display: { xs: 'flex', md: 'none' } }}>
            {logs.map((log) => (
              <Paper key={log.id} elevation={0} sx={{ ...cardSx, p: 1.25 }}>
                <Stack spacing={0.8}>
                  <Box>
                    <Typography sx={mobileLabelSx}>Action</Typography>
                    <Typography sx={{ fontSize: '0.84rem', fontWeight: 900 }}>
                      {log.action}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box>
                    <Typography sx={mobileLabelSx}>When</Typography>
                    <Typography sx={{ fontFamily: "'IBM Plex Mono', 'DM Mono', monospace", fontSize: '0.72rem', color: 'secondary.main' }}>
                      {formatAuditDate(log.createdAt)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={mobileLabelSx}>Details</Typography>
                    <Typography sx={{ fontSize: '0.78rem', color: 'text.primary' }}>
                      {formatAuditDetails(log.details)}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>

          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              siblingCount={1}
              boundaryCount={1}
              size="small"
            />
          </Box>
        </>
      )}
    </ConsolePageShell>
  );
};
import { formatDateTimeDDMMYY } from '../../utils/dateFormat';
